import { gql } from "@apollo/client";
import { useLazyQuery } from "@apollo/client/react";
import { mapTeamData } from "@components/team/TeamManagement/utils";
import { useLocalStorage } from "@scribenote/utils";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
} from "react";

import { useAuth } from "./use-auth";
import { useWatchUserWsEvent } from "./use-watch-user-ws-event";

const GET_USER_TEAMS_DETAILS = gql`
  query GetUserTeamsDetails {
    user {
      uuid
      teams {
        name
        uuid
        createdAt
        location
        organization {
          uuid
          name
          allowsDirectMemberAdd
          emailDomain
          billingModeId
        }
        userTeams {
          roleId
          isAdmin
          isSelfSubscribed
          user {
            uuid
            email
            firstName
            lastName
            account {
              uuid
              accountType
            }
          }
        }
      }
    }
  }
`;

const EMPTY_TEAM = {
  name: "",
  users: [],
  notes: [],
  uuid: null,
  id: null,
};

// Initialize context with a default value
const TeamContext = createContext({
  currentTeam: EMPTY_TEAM,
  setCurrentTeam: () => {},
  refreshUserTeams: () => {},
  selectedTeamMembers: [],
  setSelectedTeamMembers: () => {},
});

export const TeamProvider = ({ children }) => {
  const { userUuid, isUserAuthenticated } = useAuth();
  const [currentTeam, setCurrentTeam] = useLocalStorage(
    "currentTeam",
    EMPTY_TEAM,
  );
  const [selectedTeamMembers, setSelectedTeamMembers] =
    useLocalStorage("selected-team-members", []);

  const [
    fetchTeamsDetails,
    { data: userTeamsDetails, refetch: refetchTeamsDetails, called },
  ] = useLazyQuery(GET_USER_TEAMS_DETAILS);

  const safeRefetchTeamsDetails = useCallback(() => {
    if (called) {
      refetchTeamsDetails();
    } else {
      fetchTeamsDetails();
    }
  }, [called, refetchTeamsDetails, fetchTeamsDetails]);

  const fetchOrRefetchTeamsDetails = useEffectEvent(
    safeRefetchTeamsDetails,
  );

  useEffect(() => {
    if (!userUuid || !isUserAuthenticated) return;
    fetchOrRefetchTeamsDetails();
  }, [userUuid, isUserAuthenticated]);

  useWatchUserWsEvent({
    onMessage: safeRefetchTeamsDetails,
    eventName: "user_update",
  });

  const userTeams = useMemo(
    () =>
      userTeamsDetails?.user?.teams?.map((team) =>
        mapTeamData({ team }),
      ) || null,
    [userTeamsDetails],
  );

  // Ensure selected team is valid and up to date before exposing it
  const filteredCurrentTeam = useMemo(() => {
    const fetchedCurrentTeam = userTeams?.filter(
      (team) => team?.uuid === currentTeam?.uuid,
    )?.[0];
    return (
      fetchedCurrentTeam ||
      userTeams?.[0] || {
        name: "",
        users: [],
        notes: [],
        uuid: null,
      }
    );
  }, [userTeams, currentTeam?.uuid]);

  // Ensure selected member IDs are valid (i.e. part of the team) before exposing them
  const filteredSelectedTeamMembers = useMemo(() => {
    if (!filteredCurrentTeam) return [];

    const validIds = new Set(
      filteredCurrentTeam.users.map((user) => user.uuid),
    );
    const selectedIds = selectedTeamMembers.filter((uuid) =>
      validIds.has(uuid),
    );
    return selectedIds.length ? selectedIds : [userUuid];
  }, [filteredCurrentTeam, selectedTeamMembers, userUuid]);

  const teamByUuid = useMemo(
    () => (teamUuid) => {
      if (!teamUuid || !userTeams) return null;
      return userTeams.find((t) => t.uuid === teamUuid) || null;
    },
    [userTeams],
  );

  const contextValue = useMemo(
    () => ({
      userTeams,
      emptyTeam: EMPTY_TEAM,
      currentTeam: filteredCurrentTeam,
      setCurrentTeam,
      refreshUserTeams: safeRefetchTeamsDetails,
      selectedTeamMembers: filteredSelectedTeamMembers,
      setSelectedTeamMembers,
      teamByUuid,
    }),
    [
      userTeams,
      filteredCurrentTeam,
      setCurrentTeam,
      safeRefetchTeamsDetails,
      filteredSelectedTeamMembers,
      setSelectedTeamMembers,
      teamByUuid,
    ],
  );

  return (
    <TeamContext.Provider value={contextValue}>
      {children}
    </TeamContext.Provider>
  );
};

export const useTeam = () => {
  return useContext(TeamContext);
};
